import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown, Checkbox, Label, Tooltip } from "flowbite-react";
import { HiInformationCircle, HiOutlineTag } from "react-icons/hi";
import { IOrder } from "../../../types";
import { OrderStatus } from "../../../types/order/IOrder";
import { Inline } from "@design-system";

// Statuses to show in the filter
const FILTER_STATUSES = [OrderStatus.New, OrderStatus.Scheduled];

const getStatusColor = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.New:
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case OrderStatus.Scheduled:
      return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
    case OrderStatus.InProgress:
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
    case OrderStatus.Completed:
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    case OrderStatus.Cancelled:
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
  }
};

export interface OrderStatusFilterDropdownProps {
  selectedStatuses: OrderStatus[];
  onStatusChange: (status: OrderStatus) => void;
  localOrders: IOrder[];
}

export const OrderStatusFilterDropdown: FC<OrderStatusFilterDropdownProps> = ({
  selectedStatuses,
  onStatusChange,
  localOrders,
}) => {
  const { t } = useTranslation(["common", "planner", "stats"]);

  return (
    <Dropdown
      label={
        <Inline gap={1.5} className="text-sm">
          <HiOutlineTag className="h-4 w-4 mr-1" />
          <span>{t("common:status")}</span>
          {selectedStatuses.length > 0 && (
            <span className="ml-1.5 bg-brand text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
              {selectedStatuses.length}
            </span>
          )}
        </Inline>
      }
      color="light"
      size="sm"
      dismissOnClick={false}
    >
      <div className="p-3 space-y-2">
        <Inline gap={2} className="mb-2">
          <div className="text-sm font-medium text-text">
            {t("planner:filter_by_status")}:
          </div>
          <Tooltip
            className="w-[300px]"
            content={t("planner:only_new_orders_movable")}
          >
            <HiInformationCircle className="h-4 w-4 text-muted hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
          </Tooltip>
        </Inline>
        {FILTER_STATUSES.map((status) => (
          <Inline key={status} gap={2}>
            <Checkbox
              id={`status-${status}`}
              color="primary"
              checked={selectedStatuses.includes(status)}
              onChange={() => onStatusChange(status)}
              className="focus:ring-0 focus:ring-offset-0 checked:bg-brand border-gray-300"
            />
            <Label htmlFor={`status-${status}`} className="text-sm">
              {t(
                `stats:orderStatus.${status === OrderStatus.InProgress ? "in_progress" : status.toLowerCase()}`,
              )}
            </Label>
            <span
              className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${getStatusColor(status)}`}
            >
              {localOrders.filter((o) => o.status === status).length}
            </span>
          </Inline>
        ))}
      </div>
    </Dropdown>
  );
};
